import { useState, useRef } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import fallbackLogo from "../assets/logo.jpg"; // local fallback

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordRef = useRef(null);
  const navigate = useNavigate();

  const CDN_LOGO = "https://meddia.istockphoto.com/id/1145435943/vector/technology-tree-icon-on-white-background-vector-illustration.jpg?s=612x612&w=0&k=20&c=uhhw6FW20Mw8dwb3brX6E3hgsii0ieAzEwwp4RJPFn8="; // replace with your actual CDN URL
  const [logoSrc, setLogoSrc] = useState(CDN_LOGO);

  const handleChange = (e) => {
    setError("");
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await api.post("/accounts/login/", formData);
      localStorage.setItem("access_token", res.data.access);
      localStorage.setItem("refresh_token", res.data.refresh);
      navigate("/cities");
    } catch (err) {
      const data = err.response?.data;
      let message = "Login failed";

      if (data?.detail) message = data.detail;
      else if (data?.non_field_errors) message = data.non_field_errors.join(" ");

      setError(message);

      setShake(true);
      setTimeout(() => setShake(false), 350);

      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div
        className={`bg-white p-8 rounded-lg shadow-lg w-full max-w-md ${
          shake ? "shake" : ""
        }`}
      >
        <div className="flex justify-center mb-6">
          <img
            src={logoSrc}
            alt="Labor Prophet Logo"
            className="h-82 w-auto transition-transform" // scaled up
            onError={() => setLogoSrc(fallbackLogo)} // fallback to local if CDN fails
          />
        </div>

        <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>

        {error && (
          <p className="text-red-600 mb-4 text-center font-medium">{error}</p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 ${
              error ? "border-red-500 focus:ring-red-400" : "focus:ring-blue-500"
            }`}
          />

          <div className="relative">
            <input
              ref={passwordRef}
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              required
              className={`w-full p-3 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                error ? "border-red-500 focus:ring-red-400" : "focus:ring-blue-500"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </button>
        </form>

        <p className="text-sm text-gray-500 mt-4 text-center">
          Forgot password?{" "}
          <a href="/forgot-password" className="text-blue-500">
            Reset
          </a>
        </p>
        <p className="text-sm text-gray-500 mt-1 text-center">
          Don’t have an account?{" "}
          <a href="/register" className="text-blue-500">
            Register
          </a>
        </p>
      </div>
    </div>
  );
}
